import { toast } from 'sonner';
import { useState, useEffect } from 'react';
import { useBoolean } from 'minimal-shared/hooks';

import { useTranslate } from 'src/locales';
import {
  useAddBalanceMutation,
  useGetLowBalanceMutation,
  useChargeEstimateMutation,
} from 'src/pages/setting/components/billing/slice/billingApi';

type UseBalanceManagerOptions = {
  /** When false, no billing/balance API calls (e.g. billing module disabled or no permission). */
  fetchEnabled?: boolean;
};

export function useBalanceManager(options?: UseBalanceManagerOptions) {
  const fetchEnabled = options?.fetchEnabled !== false;
  const { t } = useTranslate('translation');

  // API Hooks
  const [getLowBalance, { data: balanceData, isLoading }] = useGetLowBalanceMutation();
  const [addBalance] = useAddBalanceMutation(); // Unused in original code block commented out, but kept for potential use
  const [chargeEstimate, { isLoading: isChargeEstimateLoading }] = useChargeEstimateMutation();

  // Local State
  const [addFundsAmount, setAddFundsAmount] = useState<string>('');
  const [isProcessingFunds, setIsProcessingFunds] = useState(false); // Used in commented code, but kept
  const [showAddFundsForm, setShowAddFundsForm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false); // Used in commented code
  const [animateAmount, setAnimateAmount] = useState(false);
  const [clickedButton, setClickedButton] = useState<number | null>(null);
  const [showMoneyEffect, setShowMoneyEffect] = useState(false); // Used in commented code
  const [estimateItem, setEstimateItem] = useState<any>(null);

  const cardDialog = useBoolean(false);
  const cardPaymentDialog = useBoolean(false);

  // Initial Fetch (only when balance UI is allowed for this user)
  useEffect(() => {
    if (!fetchEnabled) return;
    getLowBalance({});
  }, [fetchEnabled, getLowBalance]);

  // Handlers
  const handleAddFunds = async (amount?: number) => {
    if (!fetchEnabled) return;
    const amountToAdd = amount || parseFloat(addFundsAmount);
    if (!amountToAdd || amountToAdd <= 0) return;

    try {
      cardDialog.onTrue();
      const res = await chargeEstimate({ items: [{ amount: amountToAdd * 100 }] }).unwrap();
      setEstimateItem(res?.data?.estimate);
    } catch (error) {
      console.error('Error estimating charge:', error);
      toast.error(t('ERROR_ESTIMATING_CHARGE'));
    }
  };

  const handleQuickAmountClick = (amount: number) => {
    setClickedButton(amount);
    setAnimateAmount(true);

    setAddFundsAmount((prev) => {
      const current = parseFloat(prev) || 0;
      return (current + amount).toString();
    });

    // Reset animations
    setTimeout(() => {
      setClickedButton(null);
      setAnimateAmount(false);
    }, 600);
  };

  const onConfirmaHandler = async (item: any) => {
    cardDialog.onFalse();

    setTimeout(() => {
      setEstimateItem(item);
      cardPaymentDialog.onTrue();
    }, 50);
  };

  const handlePaymentClose = () => {
    cardPaymentDialog.onFalse();
    if (fetchEnabled) {
      getLowBalance({});
    }
    setAddFundsAmount('');
    setShowAddFundsForm(false);
    setEstimateItem(null);
  };

  const onSuccess = (data: { id: string; status: string }) => {
    const { status } = data;

    if (status === 'paid') {
      toast.success(t('PAYMENT_SUCCESS'));
    } else if (status === 'failed' || status === 'cancelled') {
      toast.error(t('PAYMENT_FAILED'));
    } else {
      console.warn('Unknown payment status:', status);
    }
  };

  return {
    // Data
    balanceData,
    isLoading,
    isChargeEstimateLoading,

    // State
    addFundsAmount,
    setAddFundsAmount,
    isProcessingFunds,
    showAddFundsForm,
    setShowAddFundsForm,
    showSuccess,
    animateAmount,
    clickedButton,
    showMoneyEffect,
    estimateItem,

    // Dialogs
    cardDialog,
    cardPaymentDialog,

    // Actions
    handleAddFunds,
    handleQuickAmountClick,
    onConfirmaHandler,
    handlePaymentClose,
    onSuccess,
    refreshBalance: () => {
      if (fetchEnabled) {
        getLowBalance({});
      }
    },
  };
}
